import type { StagePerson } from "@/components/camp/FireStage";
import type { RoomStagePerson } from "@/components/camp/RoomModeStage";
import type { AnimalId, ColourId } from "@/components/camp/campData";
import type { RoomMode } from "@/engine/state/roomControl";

/**
 * The people the preview of a room is played with (#779): the visitor, and
 * five others to meet. Names are people's names, not copy.
 */
interface Castmate {
  id: string;
  name: string;
  animal: AnimalId;
  colour: ColourId;
  /** Where they stand in a team room. */
  team: "one" | "two" | "hall";
}

export const CAST: readonly [Castmate, ...Castmate[]] = [
  { id: "mira", name: "Mira", animal: "fox", colour: "rust", team: "one" },
  { id: "timur", name: "Timur", animal: "owl", colour: "teal", team: "one" },
  { id: "ana", name: "Ana", animal: "bear", colour: "honey", team: "two" },
  { id: "leo", name: "Leo", animal: "wolf", colour: "sky", team: "two" },
  {
    id: "sasha",
    name: "Sasha",
    animal: "turtle",
    colour: "moss",
    team: "hall",
  },
];

export interface You {
  name: string;
  animal: AnimalId;
  colour: ColourId;
}

/** What floats up over somebody, by beat. */
const REACTIONS: readonly [string, ...string[]] = [
  "🔥",
  "👏",
  "😂",
  "❤️",
  "🎉",
  "👍",
];

/**
 * Seats round the fire, spread evenly, the visitor's among them: never 0, the
 * front of the ring, where a figure stands between the viewer and the fire.
 */
const SEATS: readonly [number, ...number[]] = [3, 5, 7, 9, 11];
const YOUR_SEAT = 1;

/** Two groups in a clearing, and the visitor walking from one to the other. */
const GROUP_A: readonly [Spot, ...Spot[]] = [
  { x: -0.7, y: -0.1 },
  { x: -0.45, y: 0.35 },
  { x: -0.85, y: 0.45 },
];
const GROUP_B: readonly [Spot, ...Spot[]] = [
  { x: 0.6, y: -0.2 },
  { x: 0.8, y: 0.3 },
];
const YOUR_WALK: readonly [Spot, ...Spot[]] = [
  { x: -0.35, y: 0.75 },
  { x: 0.05, y: 0.8 },
  { x: 0.45, y: 0.6 },
  { x: 0.05, y: 0.8 },
];

/** A spot on the ring's plane; see `StagePerson.position`. */
type Spot = { x: number; y: number };

/** The item a beat lands on, going round the list as the beats go on. */
export function at<T>(list: readonly [T, ...T[]], index: number): T {
  return list[((index % list.length) + list.length) % list.length] ?? list[0];
}

/** Who is talking on this beat: round the cast, the visitor every so often. */
function speakerOf(beat: number): string {
  return beat % 6 === 5 ? "you" : at(CAST, beat).id;
}

/** The fire, or a clearing walked about in, on this beat. */
export function fireCast(
  you: You,
  beat: number,
  mode: "fire" | "bubbles",
  burst: boolean,
): StagePerson[] {
  const speaker = speakerOf(beat);
  const walk = at(YOUR_WALK, Math.floor(beat / 2));
  const others = CAST.map((mate, index): StagePerson => {
    const reaction =
      burst || (beat + index) % 4 === 0
        ? at(REACTIONS, beat + index)
        : undefined;
    const person: StagePerson = {
      id: mate.id,
      name: mate.name,
      beast: mate.animal,
      colour: mate.colour,
      speaking: speaker === mate.id,
      reaction,
      raised: (beat + index) % 7 === 3,
    };
    if (mode === "fire") return { ...person, seat: at(SEATS, index) };
    const group = index < GROUP_A.length ? GROUP_A : GROUP_B;
    const spot = at(group, index < GROUP_A.length ? index : index - 3);
    // Out of earshot is faded: the far group is heard by nobody near you.
    const near = Math.abs(spot.x - walk.x) < 0.6;
    return { ...person, position: spot, faded: !near };
  });
  const yours: StagePerson = {
    id: "you",
    name: you.name,
    beast: you.animal,
    colour: you.colour,
    isYou: true,
    speaking: speaker === "you",
    reaction: burst ? "👋" : undefined,
    ...(mode === "fire" ? { seat: YOUR_SEAT } : { position: walk }),
  };
  return [yours, ...others];
}

/** The grid, or the three bands of a team room, on this beat. */
export function gridCast(you: You, beat: number): RoomStagePerson[] {
  const speaker = speakerOf(beat);
  // Somebody crosses from the hall into a team, and back, as the beats go.
  const crossing = at(CAST, Math.floor(beat / 3)).id;

  const others = CAST.map((mate): RoomStagePerson => {
    return {
      id: mate.id,
      name: mate.name,
      animal: mate.animal,
      colour: mate.colour,
      speaking: speaker === mate.id,
      team: mate.id === crossing && beat % 2 === 1 ? "hall" : mate.team,
    };
  });
  return [
    {
      id: "you",
      name: you.name,
      animal: you.animal,
      colour: you.colour,
      isYou: true,
      speaking: speaker === "you",
      team: "hall",
    },
    ...others,
  ];
}

/** The fire stage's own word for a room kind, where it draws one. */
export function fireStageMode(mode: RoomMode): "fire" | "bubbles" | null {
  if (mode === "campfire") return "fire";
  if (mode === "bubbles") return "bubbles";
  return null;
}
